import { PureComponent, type ReactNode } from "react";
import type { ApolloClient } from "@apollo/client";
import moment from "moment";
import { get, find } from "lodash";
import { getMetaValue, getInitials, detectFieldChanges } from "utils/typeUtils";
import type { User, ApprovalItem } from "types/models";

import ComparisonCard from "./ComparisonCard";
import AccountHolderInfoCard from "./AccountHolderInfo";

// Address shape built from getMetaValue() — every field is `unknown` (the
// metadata value type). Only string-interpolated/filtered here, so `unknown`
// is sufficient and faithful to the original untyped usage.
interface USAddress {
  line1?: unknown;
  line2?: unknown;
  city?: unknown;
  state?: unknown;
  zip?: unknown;
}

interface AddressApprovalProps {
  // Injected by withApollo where this component is used; declared but not read
  // in this render body (matches the original PropTypes `client: object`).
  client?: ApolloClient<object>;
  approval?: ApprovalItem;
  user?: User;
}

const formatUSAddress = (address: USAddress) => {
  const line1 = get(address, "line1", "");
  const line2 = get(address, "line2");
  const city = get(address, "city", "");
  const state = address.state;
  const zip = address.zip;

  const streetLine = [line1, line2].filter(Boolean).join(" ").trim();
  const cityStateZip =
    `${String(city)}, ${String(state)} ${String(zip)}`.trim();

  return [streetLine, cityStateZip].filter(Boolean).join("\n");
};

class AddressApproval extends PureComponent<AddressApprovalProps> {
  render() {
    const dob = get(this.props.user, "userProfile.dob");
    const ssn = get(this.props.user, "fullSsn");
    const phone = get(this.props.user, "userProfile.phoneNumber");
    const kycState = get(this.props.user, "user.state");
    const silaWalletID = get(
      // `user` is optional but the original JS dereferences `.accounts`
      // unconditionally (throws if absent); preserve that with `!`. The shared
      // `User` model (codegen) omits `accounts`; the original JS read it as a
      // loose array, so cast to a local accounts-bearing shape at this boundary
      // (mirrors NameChangeApproval) — runtime read unchanged.
      find(
        (this.props.user! as { accounts?: Record<string, unknown>[] }).accounts,
        { accountType: "WALLET" }
      ),
      "accountNumber",
      null
    );

    // lodash `get` widens to `unknown`; getMetaValue accepts the metadata
    // item array shape, so re-narrow at this boundary.
    const metadataItems = get(
      this.props.approval,
      "metadataItems",
      []
    ) as Parameters<typeof getMetaValue>[0];

    const currentAddressObj = {
      line1: getMetaValue(metadataItems, "Current Address 1"),
      line2: getMetaValue(metadataItems, "Current Address 2"),
      city: getMetaValue(metadataItems, "Current City"),
      state: getMetaValue(metadataItems, "Current State"),
      zip: getMetaValue(metadataItems, "Current Postal Code"),
    };

    const newAddressObj = {
      line1: getMetaValue(metadataItems, "New Address 1"),
      line2: getMetaValue(metadataItems, "New Address 2"),
      city: getMetaValue(metadataItems, "New City"),
      state: getMetaValue(metadataItems, "New State"),
      zip: getMetaValue(metadataItems, "New Postal Code"),
    };

    const currentAddress = formatUSAddress(currentAddressObj);
    const newAddress = formatUSAddress(newAddressObj);

    const fields = {
      line1: "Address line 1",
      line2: "Address line 2",
      city: "City",
      state: "State",
      zip: "Postal Code",
    };

    const addressChanges = detectFieldChanges(
      currentAddressObj,
      newAddressObj,
      fields
    );

    const firstName = getMetaValue(metadataItems, "Account Holder First Name");
    const lastName = getMetaValue(metadataItems, "Account Holder Last Name");
    const submissionDate = getMetaValue(metadataItems, "Submission Date");
    const apexAccount = getMetaValue(metadataItems, "Apex Account Number");

    const email = get(this.props.approval, "correlationName", null);

    // getMetaValue returns `unknown`; the metadata values are name strings at
    // runtime, which getInitials expects.
    const initials = getInitials(
      firstName as string | null | undefined,
      lastName as string | null | undefined
    );

    const formattedDate = submissionDate
      ? // submissionDate is `unknown` from getMetaValue; a date string at runtime.
        moment(submissionDate as moment.MomentInput).format(
          "dddd, MMMM Do YYYY"
        )
      : null;

    const uploadRetries = get(
      this.props,
      "user.documents[0].metadata.batchUploadCount",
      "0"
    );

    return (
      <>
        {/* getMetaValue / loose lookups below return `unknown` (name/account
            strings at runtime), rendered as InfoItem values (React nodes). The
            ReactNode casts preserve the exact runtime values the original JS
            passed. */}
        <AccountHolderInfoCard
          initials={initials}
          firstName={firstName as ReactNode}
          lastName={lastName as ReactNode}
          email={email}
          dob={dob}
          ssn={ssn}
          userId={get(this.props.approval, "entityIdentifier.id", "N/A")}
          submissionDate={formattedDate}
          apexAccount={apexAccount as ReactNode}
          uploadRetries={uploadRetries as ReactNode}
          phone={phone}
          kycState={kycState}
          kycStatus={kycState}
          silaWalletID={silaWalletID as ReactNode}
        />

        <ComparisonCard
          title="Address Comparison"
          changeType="address"
          currentLabel="Current Address"
          newChangeLabel="New Address"
          current={currentAddress}
          newChange={newAddress}
          changes={addressChanges}
        />
      </>
    );
  }
}

export default AddressApproval;
